import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BookmarkSimple, Eye, ArrowUpRight, MagnifyingGlassPlus, X, CaretLeft, CaretRight, Clock, Lightbulb } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { FIT_BADGE } from "@/lib/fit";
import { formatHeight } from "@/lib/heights";
import InlineBanner from "@/components/InlineBanner";
import HeightRecommendations from "@/components/HeightRecommendations";
import { track, itemFromProduct } from "@/lib/track";
import { usePageMeta, snippet } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Community fit-share is hidden for now — flip to true to bring it back.
const SHOW_COMMUNITY_FIT = false;

// Heading out to the retailer is the closest thing this site has to a
// conversion. GA4 has no recommended name for an affiliate/outbound handoff
// (add_to_cart would be a lie and would pollute the ecommerce reports), so this
// stays a clearly-named custom event — with the item payload shaped like every
// other item event so the funnel still stitches together.
const trackProductClick = (product) => {
  if (!product) return;
  track("outbound_click", {
    link_url: product.product_link,
    link_domain: (() => {
      try {
        return new URL(product.product_link).hostname;
      } catch (_) {
        return undefined;
      }
    })(),
    outbound: true,
    items: [itemFromProduct(product)],
    // Legacy params, so the dual-fired view_product_link_click is unchanged.
    product_name: product.product_name,
    product_category: product.category,
    product_link: product.product_link,
    image_link: product.image_url,
    event_category: "Product Engagement",
    event_label: product.product_name,
  });
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [views, setViews] = useState(0);
  const [bookmarked, setBookmarked] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);
  const [lightbox, setLightbox] = useState(false); // full-screen image viewer
  // Hotlinked retailer images can rotate/expire — track any that fail to load so
  // we can drop them and fall back to the image we stored ourselves.
  const [brokenImgs, setBrokenImgs] = useState(() => new Set());
  const [campaigns, setCampaigns] = useState([]); // targeted marketing banners

  // Always land at the top — the feed's scroll position shouldn't carry over,
  // and neither should the previous product's when navigating detail → detail.
  // useLayoutEffect runs before paint, so there's no mid-page flash.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  }, [id]);

  // One targeted marketing banner slot, matched to this product's category.
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    axios
      .get(`${API}/campaigns/feed`, {
        params: { category_id: product.category_id || "all" },
        withCredentials: true,
      })
      .then((res) => !cancelled && setCampaigns(res.data || []))
      .catch(() => !cancelled && setCampaigns([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`, { withCredentials: true });
        if (cancelled) return;
        const p = res.data || {};
        setProduct(p);
        setViews(p.views || 0);
        setBookmarked(!!p.bookmarked);
        setSelectedImg(0);
        setBrokenImgs(new Set());

        // GA4 view_item — the detail-page half of the ecommerce funnel, so
        // view_item_list -> select_item -> view_item -> outbound_click lines up.
        track("view_item", {
          items: [itemFromProduct(p)],
          value: typeof p.price === "number" ? p.price : undefined,
          currency: typeof p.price === "number" ? "INR" : undefined,
          gallery_size: (p.image_urls || []).length,
        });

        // Count this visit once per browser session, so a refresh doesn't inflate
        // the number. Browsing is free — no sign-in needed to be counted.
        const seenKey = `nos_viewed_${id}`;
        if (!sessionStorage.getItem(seenKey)) {
          sessionStorage.setItem(seenKey, "1");
          try {
            const r = await axios.post(`${API}/products/${id}/view`);
            if (!cancelled && typeof r.data?.views === "number") setViews(r.data.views);
          } catch (_) {
            /* non-fatal: the count just won't tick up this time */
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error("Error fetching product:", err);
          toast.error("Could not load this product");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleToggleBookmark = async () => {
    const next = !bookmarked;
    setBookmarked(next); // optimistic
    try {
      const res = await axios.post(
        `${API}/products/${id}/bookmark`,
        {},
        { withCredentials: true }
      );
      const isSaved = !!res.data?.bookmarked;
      setBookmarked(isSaved);
      // GA4 has add_to_wishlist but no removal counterpart, so the remove side
      // is a matching custom name rather than a bent standard one.
      track(isSaved ? "add_to_wishlist" : "remove_from_wishlist", {
        items: [itemFromProduct(product)],
        value: typeof product?.price === "number" ? product.price : undefined,
        currency: typeof product?.price === "number" ? "INR" : undefined,
        source: "product_detail",
      });
      toast.success(isSaved ? "Saved to bookmarks" : "Removed from bookmarks");
    } catch (err) {
      setBookmarked(!next); // revert
      if (err?.response?.status === 401) {
        // A signed-out save attempt is intent we were previously blind to.
        track("wishlist_blocked_signed_out", { items: [itemFromProduct(product)] });
        toast.error("Please sign in to bookmark");
        navigate("/login");
        return;
      }
      toast.error("Could not update bookmark");
    }
  };

  const handleViewAtRetailer = () => {
    trackProductClick(product);
    window.open(product.product_link, "_blank", "noopener,noreferrer");
  };

  // Size badge honours the product's own category (shoes → "UK 9", clothing → "9 ft").
  const cat = product?.category_info;
  const sizeLabel = (cat?.size_label || "ft").trim() || "ft";
  const isShoe = cat?.kind === "shoes";
  const sizes = (product?.heights || []).join(", ");
  const fs = product?.fit_share;

  // Search-result title/description: the product under its own name and the
  // sizes it fits, e.g. "Levi's 511 Slim — fits 6'2", 6'3"".
  const sizeText = isShoe
    ? `UK ${(product?.heights || []).join(", ")}`
    : (product?.heights || []).map(formatHeight).join(", ");
  usePageMeta(
    product ? `${product.product_name}${sizeText ? ` — fits ${sizeText}` : ""}` : null,
    product
      ? snippet(
          `${product.product_name}${product.brand?.name ? ` by ${product.brand.name}` : ""}` +
            `${sizeText ? `, tried and shared by tall ${product.gender === "women" ? "women" : "men"} at ${sizeText}` : ""}.` +
            ` See photos, price and where to buy on North of Six Club.`
        )
      : null
  );

  // Gallery: scraped retailer images (absolute URLs) first, dropping any that
  // failed to load. If none survive, fall back to the image we stored ourselves.
  // If that's gone too, we show no image at all.
  const storedImg = product?.image_url ? `${BACKEND_URL}${product.image_url}` : null;
  const scraped = (product?.image_urls || []).filter((u) => !brokenImgs.has(u));
  const gallery =
    scraped.length > 0
      ? scraped
      : storedImg && !brokenImgs.has(storedImg)
      ? [storedImg]
      : [];
  const mainImg = gallery[Math.min(selectedImg, gallery.length - 1)] || null;
  const markBroken = (url) =>
    setBrokenImgs((prev) => {
      const next = new Set(prev);
      next.add(url);
      return next;
    });

  const galleryLen = gallery.length;
  const scrollRef = useRef(null); // inline horizontal carousel
  const lightboxRef = useRef(null); // full-screen horizontal carousel
  // Scroll the inline carousel to a given slide (used by the thumbnails).
  const scrollToImg = (i) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };
  // Page the full-screen viewer (arrow buttons + keyboard).
  const lightboxTo = (i, method = "lightbox_arrow") => {
    const next = (i + galleryLen) % galleryLen;
    setSelectedImg(next);
    track("gallery_image_change", {
      item_id: product?.id,
      image_index: next,
      method,
    });
    const el = lightboxRef.current;
    if (el) el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
  };
  const showPrev = (method) => lightboxTo(Math.min(selectedImg, galleryLen - 1) - 1, method);
  const showNext = (method) => lightboxTo(Math.min(selectedImg, galleryLen - 1) + 1, method);

  // When the viewer opens, jump it straight to the current image (no animation).
  useEffect(() => {
    if (!lightbox) return;
    const el = lightboxRef.current;
    if (el) el.scrollLeft = Math.min(selectedImg, galleryLen - 1) * el.clientWidth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  // Escape closes the lightbox; arrows page through the gallery inside it.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowRight" && galleryLen > 1) showNext("keyboard");
      else if (e.key === "ArrowLeft" && galleryLen > 1) showPrev("keyboard");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, galleryLen]);

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#E4E1D9] px-6 md:px-12 py-4 flex items-center gap-4">
        <button
          type="button"
          onClick={() => {
            track("back_to_feed", { item_id: product?.id, from: "product_detail" });
            navigate(-1);
          }}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="product-detail-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1
          className="text-2xl md:text-3xl tracking-tighter ml-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          North of Six Club
        </h1>
        <button
          type="button"
          onClick={() => {
            track("inseam_guide_open", { source: "product_detail" });
            navigate("/inseam-guide");
          }}
          className="ml-auto p-2 hover:bg-[#F0EFEB] rounded-md transition-colors"
          title="Inseam guide — how to measure & find your fit"
          aria-label="Inseam guide"
          data-testid="inseam-guide-button"
        >
          <Lightbulb size={22} weight="fill" className="text-[#2D302A]" />
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-4 md:py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 animate-pulse">
            <div className="w-full h-[44vh] md:h-[70vh] max-h-[640px] bg-[#E4E1D9] rounded-md" />
            <div className="space-y-4 pt-4">
              <div className="h-8 bg-[#E4E1D9] rounded w-3/4" />
              <div className="h-4 bg-[#E4E1D9] rounded w-1/3" />
              <div className="h-4 bg-[#E4E1D9] rounded w-1/2" />
              <div className="h-12 bg-[#E4E1D9] rounded w-full mt-8" />
            </div>
          </div>
        ) : notFound ? (
          <div className="text-center py-20">
            <p className="text-[#6B6A68] mb-6">This product isn't available anymore.</p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-[0.2em] text-[#2D302A] hover:opacity-70 transition-opacity"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Browse products →
            </button>
          </div>
        ) : product ? (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Image gallery — omitted entirely when no image loads */}
            {mainImg && (
            <div>
            <div className="group/img relative">
              {/* Swipeable horizontal carousel — one image per view, snap on swipe */}
              <div
                ref={scrollRef}
                onScroll={(e) => {
                  const w = e.currentTarget.clientWidth || 1;
                  const i = Math.round(e.currentTarget.scrollLeft / w);
                  if (i !== Math.min(selectedImg, galleryLen - 1)) setSelectedImg(i);
                }}
                className="flex overflow-x-auto snap-x snap-mandatory bg-[#F0EFEB] border border-[#E4E1D9] h-[44vh] md:h-[70vh] max-h-[640px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                data-testid="product-detail-carousel"
              >
                {gallery.map((url, i) => (
                  <div key={url} className="snap-center shrink-0 w-full h-full flex items-center justify-center">
                    <img
                      src={url}
                      alt={product.product_name}
                      className="max-h-full max-w-full object-contain cursor-zoom-in"
                      onClick={() => {
                        setSelectedImg(i);
                        setLightbox(true);
                        track("gallery_lightbox_open", {
                          item_id: product?.id,
                          image_index: i,
                          gallery_size: galleryLen,
                        });
                      }}
                      onError={() => markBroken(url)}
                      data-testid={i === 0 ? "product-detail-image" : undefined}
                    />
                  </div>
                ))}
              </div>
              {/* Zoom hint */}
              <span
                className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1 shadow text-[10px] uppercase tracking-[0.1em] text-[#2D302A] opacity-0 group-hover/img:opacity-100 transition-opacity"
                style={{ fontFamily: "IBM Plex Mono, monospace" }}
              >
                <MagnifyingGlassPlus size={12} /> View full
              </span>
              {/* Brand tag */}
              {product.brand && (
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-full p-1 pr-2.5 shadow-md">
                  {product.brand.logo_url && (
                    <img
                      src={`${BACKEND_URL}${product.brand.logo_url}`}
                      alt={product.brand.name}
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  )}
                  <span
                    className="text-[10px] uppercase tracking-[0.1em] text-[#2D302A]"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    {product.brand.name}
                  </span>
                </div>
              )}
              {/* Bookmark toggle */}
              <button
                type="button"
                onClick={handleToggleBookmark}
                className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm hover:bg-white p-2 rounded-full shadow-lg transition-all hover:scale-110"
                aria-label={bookmarked ? "Remove bookmark" : "Bookmark this product"}
                aria-pressed={bookmarked}
                title={bookmarked ? "Remove bookmark" : "Bookmark"}
                data-testid="product-detail-bookmark"
              >
                <BookmarkSimple size={20} weight={bookmarked ? "fill" : "regular"} className="text-[#2D302A]" />
              </button>
              {/* Slide dots */}
              {galleryLen > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {gallery.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${
                        i === Math.min(selectedImg, galleryLen - 1) ? "w-4 bg-[#2D302A]" : "w-1.5 bg-[#2D302A]/40"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Thumbnail strip — only when there's more than one image */}
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="product-detail-thumbs">
                {gallery.map((url, i) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => {
                      setSelectedImg(i);
                      scrollToImg(i);
                      track("gallery_image_change", {
                        item_id: product?.id,
                        image_index: i,
                        method: "thumbnail",
                      });
                    }}
                    className={`shrink-0 w-14 aspect-[3/4] overflow-hidden border transition-all ${
                      i === Math.min(selectedImg, gallery.length - 1)
                        ? "border-[#2D302A]"
                        : "border-[#E4E1D9] opacity-70 hover:opacity-100"
                    }`}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img
                      src={url}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={() => markBroken(url)}
                    />
                  </button>
                ))}
              </div>
            )}
            </div>
            )}

            {/* Info */}
            <div className="flex flex-col">
              <h2
                className="text-3xl md:text-4xl tracking-tight leading-tight"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
                data-testid="product-detail-name"
              >
                {product.product_name}
              </h2>

              {product.user_name && (
                <p className="text-sm text-[#6B6A68] mt-2">Shared by a community member</p>
              )}

              {/* Visit count — until 30 views we show "Recently posted" instead
                  of a small, unflattering number; past 30 it becomes a real
                  popularity signal. */}
              <div
                className="flex items-center gap-2 mt-3 text-[#2D302A]"
                data-testid="product-detail-views"
              >
                {views < 30 ? (
                  <Clock size={18} weight="regular" className="text-[#6B6A68]" />
                ) : (
                  <Eye size={18} weight="regular" className="text-[#6B6A68]" />
                )}
                <span className="text-sm" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
                  {views < 30 ? "Recently posted" : `${views.toLocaleString()} people viewed this`}
                </span>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mt-5">
                {product.price && (
                  <span
                    className="text-xs uppercase tracking-[0.1em] text-white bg-[#2D302A] px-3 py-1 rounded-sm"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    data-testid="product-detail-price"
                  >
                    {product.price}
                  </span>
                )}
                {sizes && (
                  <span
                    className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] bg-[#F0EFEB] px-3 py-1 rounded-sm"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    {isShoe ? `${sizeLabel} ${sizes}` : `${sizes} ${sizeLabel}`}
                  </span>
                )}
                {product.fit_feedback && FIT_BADGE[product.fit_feedback] && (
                  <span
                    className="text-xs lowercase text-[#2D302A] bg-[#E9E3D6] px-3 py-1 rounded-sm"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    {FIT_BADGE[product.fit_feedback]}
                  </span>
                )}
              </div>

              {/* Community fit-share (hidden for now via SHOW_COMMUNITY_FIT) */}
              {SHOW_COMMUNITY_FIT && fs && (fs.height || fs.waist_size || fs.shoe_size || fs.fit_feedback) && (
                <div className="mt-8" data-testid="product-detail-fitshare">
                  <p
                    className="text-[10px] uppercase tracking-[0.3em] text-[#6B6A68] mb-3"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    Community fit
                  </p>
                  <p className="text-sm text-[#2D302A] leading-relaxed">
                    Shared by <span className="font-medium">a community member</span>
                    {fs.height ? `, who is ${formatHeight(fs.height)} tall` : ""}.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {fs.waist_size && (
                      <span
                        className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] bg-[#F0EFEB] px-3 py-1 rounded-sm"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      >
                        waist {fs.waist_size}"
                      </span>
                    )}
                    {fs.shoe_size && (
                      <span
                        className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] bg-[#F0EFEB] px-3 py-1 rounded-sm"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      >
                        shoe UK {fs.shoe_size}
                      </span>
                    )}
                    {fs.fit_feedback && FIT_BADGE[fs.fit_feedback] && (
                      <span
                        className="text-xs lowercase text-[#2D302A] bg-[#E9E3D6] px-3 py-1 rounded-sm"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      >
                        {FIT_BADGE[fs.fit_feedback]}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* View at retailer */}
              <button
                type="button"
                onClick={handleViewAtRetailer}
                className="mt-6 md:mt-8 inline-flex items-center justify-center gap-2 bg-[#2D302A] text-white px-6 py-3 rounded-sm hover:bg-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
                style={{ fontFamily: "IBM Plex Mono, monospace" }}
                data-testid="product-detail-retailer"
              >
                {product.brand?.logo_url && (
                  <img
                    src={`${BACKEND_URL}${product.brand.logo_url}`}
                    alt={product.brand.name || ""}
                    className="w-5 h-5 rounded-full object-cover bg-white shrink-0"
                  />
                )}
                View at retailer <ArrowUpRight size={16} weight="bold" />
              </button>

              {/* Share-to-earn announcement */}
              <button
                type="button"
                onClick={() => {
                  track("share_fit_click", { source: "product_detail_reward" });
                  navigate("/", { state: { openCreate: true } });
                }}
                className="mt-6 w-full flex items-center gap-3 text-left bg-[#FAF9F5] border border-[#E4E1D9] rounded-md p-3 hover:bg-[#F0EFEB] transition-colors"
                data-testid="product-detail-reward"
              >
                <img
                  src="/amazon-gift-card.png"
                  alt="Amazon Gift Card"
                  className="w-14 h-9 rounded object-cover shrink-0"
                />
                <p className="text-sm leading-snug text-[#2D302A]">
                  🎁 Share a product, earn a <strong>₹10 Amazon Gift Card</strong> for every
                  verified post with the correct size - no limit, just keep sharing!
                </p>
              </button>

              {/* One targeted campaign slot, below the retailer button */}
              {campaigns[0] && (
                <div className="mt-6">
                  <InlineBanner
                    campaign={campaigns[0]}
                    onShareClick={() => {
                      track("share_fit_click", { source: "product_detail_banner" });
                      navigate("/", { state: { openCreate: true } });
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Height-aware "more like this" strip */}
          <HeightRecommendations product={product} />

          {/* Contribute CTA — same block as the homepage feed footer */}
          <div className="mt-14 md:mt-20 border-t border-[#E4E1D9] pt-12 md:pt-16 text-center max-w-2xl mx-auto">
            <h2
              className="text-3xl md:text-5xl tracking-tighter leading-[1.05] mb-4"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Other Bro's are waiting 🥸
            </h2>
            <p className="text-[#6B6A68] text-base md:text-lg mb-7 max-w-xl mx-auto">
              {isShoe
                ? "Share the shoes that actually fit your size - you’ll save someone the time and money of guessing what works for them."
                : "Share the fits that actually work for your height - you’ll save someone the time and money of guessing what will fit them."}
            </p>
            <button
              type="button"
              onClick={() => {
                track("share_fit_click", { source: "product_detail_cta", item_id: product?.id });
                navigate("/", { state: { openCreate: true } });
              }}
              className="bg-[#2D302A] text-white px-8 py-4 rounded-md hover:bg-[#1A1C18] transition-colors duration-300"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
              data-testid="product-detail-contribute"
            >
              <span className="text-xs uppercase tracking-[0.2em]">
                Share {isShoe ? "Your Shoes" : "a Fit"}
              </span>
            </button>
          </div>
          </>
        ) : null}
      </main>

      {/* Full-screen image viewer — click backdrop / ✕ / Escape to close */}
      {lightbox && mainImg && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightbox(false)}
          role="dialog"
          aria-modal="true"
          data-testid="product-detail-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            aria-label="Close"
          >
            <X size={28} weight="bold" />
          </button>
          {galleryLen > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); showPrev("lightbox_arrow"); }}
                className="absolute left-2 md:left-6 z-10 text-white/80 hover:text-white p-2"
                aria-label="Previous image"
              >
                <CaretLeft size={32} weight="bold" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); showNext("lightbox_arrow"); }}
                className="absolute right-2 md:right-6 z-10 text-white/80 hover:text-white p-2"
                aria-label="Next image"
              >
                <CaretRight size={32} weight="bold" />
              </button>
            </>
          )}
          {/* Swipeable full-screen carousel */}
          <div
            ref={lightboxRef}
            onClick={() => setLightbox(false)}
            onScroll={(e) => {
              const w = e.currentTarget.clientWidth || 1;
              const i = Math.round(e.currentTarget.scrollLeft / w);
              if (i !== Math.min(selectedImg, galleryLen - 1)) setSelectedImg(i);
            }}
            className="w-full h-full flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {gallery.map((url, i) => (
              <div key={url} className="snap-center shrink-0 w-full h-full flex items-center justify-center px-4">
                <img
                  src={url}
                  alt={product?.product_name || ""}
                  className="max-h-[90vh] max-w-full object-contain"
                  onClick={(e) => e.stopPropagation()}
                  onError={() => markBroken(url)}
                />
              </div>
            ))}
          </div>
          {galleryLen > 1 && (
            <span
              className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              {Math.min(selectedImg, galleryLen - 1) + 1} / {galleryLen}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
